import { yupResolver } from "@hookform/resolvers/yup";
import { useBoolean } from "hooks";
import { ProductsSelector } from "modules/products";
import { Controller, FormProvider, Resolver, useForm } from "react-hook-form";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Modal } from "ui";

import {
  useCreateSharingSchedule,
  useUpdateSharingSchedule
} from "../../../apis";
import { EMPTY_SHARING_SCHEDULE_FORM_VALUES } from "../../../constants";
import {
  convertSharingScheduleFormDataToCreateRequestBody,
  convertSharingScheduleFormDataToUpdateRequestBody,
  getEditableScheduleDefaultFormData,
  sharingScheduleValidationSchema
} from "../../../helpers";
import { SharingScheduleForm, SharingScheduleFormValues } from "../../forms";
import { ScheduleModalProps } from "./ScheduleModal.types";

const SHARING_SCHEDULE_FORM_ID = "SHARING_SCHEDULE_FORM_ID";

const ScheduleModal = ({
  isOpen,
  editableSchedule,
  onClose,
  refetchSchedule
}: ScheduleModalProps) => {
  const { t } = useTranslation();

  const form = useForm({
    defaultValues: editableSchedule
      ? getEditableScheduleDefaultFormData(editableSchedule)
      : EMPTY_SHARING_SCHEDULE_FORM_VALUES,
    resolver: yupResolver(sharingScheduleValidationSchema) as Resolver<any, any>
  });

  const {
    value: isSelectProductsModalOpen,
    setTrue: openSelectProductsModal,
    setFalse: closeSelectProductsModal
  } = useBoolean(false);

  const createSharingSchedule = useCreateSharingSchedule({
    onSuccess: () => {
      refetchSchedule();
      onClose();
      toast.success(t("Sharing Schedule created successfully"));
    }
  });

  const updateSharingSchedule = useUpdateSharingSchedule({
    onSuccess: () => {
      refetchSchedule();
      onClose();
      toast.success(t("Sharing Schedule updated successfully"));
    }
  });

  const handleFormSubmit = (formData: SharingScheduleFormValues) => {
    if (formData.id) {
      updateSharingSchedule.mutate(
        convertSharingScheduleFormDataToUpdateRequestBody(formData)
      );
    } else {
      createSharingSchedule.mutate(
        convertSharingScheduleFormDataToCreateRequestBody(formData)
      );
    }
  };

  const numberOfProductsWatch = form.watch("numberOfProducts");

  return (
    <FormProvider {...form}>
      {!isSelectProductsModalOpen ? (
        <Modal
          formId={SHARING_SCHEDULE_FORM_ID}
          title={editableSchedule ? t("Edit Schedule") : t("New Schedule")}
          isOpen={isOpen}
          onClose={onClose}
          isLoading={
            createSharingSchedule.isLoading || updateSharingSchedule.isLoading
          }
          okText={editableSchedule ? t("Edit") : t("Create")}
        >
          <SharingScheduleForm
            formId={SHARING_SCHEDULE_FORM_ID}
            onSelectProductsClick={openSelectProductsModal}
            onSubmit={handleFormSubmit}
            editableSchedule={editableSchedule}
          />
        </Modal>
      ) : (
        <Controller
          control={form.control}
          name={"productVariants"}
          render={({ field: { value, onChange } }) => (
            <ProductsSelector
              isOpen={isSelectProductsModalOpen}
              onClose={closeSelectProductsModal}
              value={value}
              onChange={onChange}
              maxValueLength={numberOfProductsWatch}
            />
          )}
        />
      )}
    </FormProvider>
  );
};

export default ScheduleModal;
